import { apiClient } from '@/lib/apiClient';

export const superAdminApi = {
  // ─────────────────────────────────────────────
  // Dashboard
  // ─────────────────────────────────────────────

  getDashboard: async () => {
    const { data, error } = await apiClient.GET(
      '/api/v1/super_admin/dashboard'
    );

    if (error) throw error;
    return data;
  },

  // ─────────────────────────────────────────────
  // Users
  // ─────────────────────────────────────────────

  getUsers: async (params) => {
    const { data, error } = await apiClient.GET(
      '/api/v1/super_admin/users',
      {
        params: {
          query: params,
        },
      }
    );

    if (error) throw error;
    return data;
  },

  updateUserStatus: async (userId, isActive) => {
    const { data, error } = await apiClient.PATCH(
      '/api/v1/super_admin/users/{user_id}/status',
      {
        params: {
          path: {
            user_id: userId,
          },
        },
        body: {
          is_active: isActive,
        },
      }
    );

    if (error) throw error;
    return data;
  },

  deleteUser: async (userId) => {
    const { data, error } = await apiClient.DELETE(
      '/api/v1/super_admin/users/{user_id}',
      {
        params: {
          path: {
            user_id: userId,
          },
        },
      }
    );

    if (error) throw error;
    return data;
  },

  // ─────────────────────────────────────────────
  // Vendors
  // ─────────────────────────────────────────────

  getVendors: async (params) => {
    const { data, error } = await apiClient.GET(
      '/api/v1/super_admin/vendors',
      {
        params: {
          query: params,
        },
      }
    );

    if (error) throw error;
    return data;
  },

  updateVendorVerification: async (vendorId, isVerified) => {
    const { data, error } = await apiClient.PATCH(
      '/api/v1/super_admin/vendors/{vendor_id}/verification',
      {
        params: {
          path: {
            vendor_id: vendorId,
          },
        },
        body: {
          is_verified: isVerified,
        },
      }
    );

    if (error) throw error;
    return data;
  },

  updateVendorCommission: async (vendorId, commissionRate) => {
    const { data, error } = await apiClient.PATCH(
      '/api/v1/super_admin/vendors/{vendor_id}/commission',
      {
        params: {
          path: {
            vendor_id: vendorId,
          },
        },
        body: {
          commission_rate: commissionRate,
        },
      }
    );

    if (error) throw error;
    return data;
  },
  
  updateVendorReferral: async (vendorId, referralEnabled) => {
    const { data, error } = await apiClient.PATCH(
      '/api/v1/super_admin/vendors/{vendor_id}/referral',
      {
        params: {
          path: {
            vendor_id: vendorId,
          },
        },
        body: {
          referral_enabled: referralEnabled,
        },
      }
    );

    if (error) throw error;
    return data;
  },

  deleteVendor: async (vendorId) => {
    const { data, error } = await apiClient.DELETE(
      '/api/v1/super_admin/vendors/{vendor_id}',
      {
        params: {
          path: {
            vendor_id: vendorId,
          },
        },
      }
    );

    if (error) throw error;
    return data;
  },

  // ─────────────────────────────────────────────
  // Irancharsouq Admins
  // ─────────────────────────────────────────────

  getAdmins: async (params) => {
    const { data, error } = await apiClient.GET(
      '/api/v1/super_admin/admins',
      {
        params: {
          query: params,
        },
      }
    );

    if (error) throw error;
    return data;
  },

  getAdmin: async (adminId) => {
    const { data, error } = await apiClient.GET(
      '/api/v1/super_admin/admins/{admin_id}',
      {
        params: {
          path: {
            admin_id: adminId,
          },
        },
      }
    );

    if (error) throw error;
    return data;
  },

  createAdmin: async (adminData) => {
    const { data, error } = await apiClient.POST(
      '/api/v1/super_admin/admins',
      {
        body: adminData,
      }
    );

    if (error) throw error;
    return data;
  },

  updateAdmin: async (adminId, adminData) => {
    const { data, error } = await apiClient.PATCH(
      '/api/v1/super_admin/admins/{admin_id}',
      {
        params: {
          path: {
            admin_id: adminId,
          },
        },
        body: adminData,
      }
    );

    if (error) throw error;
    return data;
  },

  deleteAdmin: async (adminId) => {
    const { data, error } = await apiClient.DELETE(
      '/api/v1/super_admin/admins/{admin_id}',
      {
        params: {
          path: {
            admin_id: adminId,
          },
        },
      }
    );

    if (error) throw error;
    return data;
  },

  updateAdminVerification: async (adminId, isVerified) => {
    const { data, error } = await apiClient.PATCH(
      '/api/v1/super_admin/admins/{admin_id}/verification',
      {
        params: {
          path: {
            admin_id: adminId,
          },
        },
        body: {
          is_verified: isVerified,
        },
      }
    );

    if (error) throw error;
    return data;
  },

  updateAdminCommission: async (adminId, commissionRate) => {
    const { data, error } = await apiClient.PATCH(
      '/api/v1/super_admin/admins/{admin_id}/commission',
      {
        params: {
          path: {
            admin_id: adminId,
          },
        },
        body: {
          commission_rate: commissionRate,
        },
      }
    );

    if (error) throw error;
    return data;
  },

  uploadAdminBanner: async (adminId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    const { data, error } = await apiClient.POST(
      '/api/v1/super_admin/admins/{admin_id}/banner',
      {
        params: { path: { admin_id: adminId } },
        body: formData,
      }
    );
    if (error) throw error;
    return data;
  },

  // ─────────────────────────────────────────────
  // Orders
  // ─────────────────────────────────────────────

  getOrders: async (params) => {
    const { data, error } = await apiClient.GET(
      '/api/v1/super_admin/orders',
      {
        params: {
          query: params,
        },
      }
    );

    if (error) throw error;
    return data;
  },

  getOrder: async (orderId) => {
    const { data, error } = await apiClient.GET(
      '/api/v1/super_admin/orders/{order_id}',
      { params: { path: { order_id: orderId } } }
    );
    if (error) throw error;
    return data;
  },

  markOrderDelivered: async (orderId) => {
    const { data, error } = await apiClient.PATCH(
      '/api/v1/super_admin/orders/{order_id}/deliver',
      { params: { path: { order_id: orderId } } }
    );

    if (error) throw error;
    return data;
  },

  previewCampaign: async (body) => {
    const { data, error } = await apiClient.POST(
      '/api/v1/super_admin/announcements/preview',
      { body },
    );
    if (error) throw error;
    return data;
  },

  getCampaignRecipients: async (body, params = { page: 1, size: 20 }) => {
    const { data, error } = await apiClient.POST(
      '/api/v1/super_admin/announcements/recipients',
      {
        params: { query: params },
        body,
      },
    );
    if (error) throw error;
    return data;
  },

  createCampaign: async (body) => {
    const { data, error } = await apiClient.POST(
      '/api/v1/super_admin/announcements',
      { body },
    );
    if (error) throw error;
    return data;
  },

  getCampaigns: async (params = {}) => {
    const { data, error } = await apiClient.GET(
      '/api/v1/super_admin/announcements',
      { params: { query: params } },
    );
    if (error) throw error;
    return data;
  },
};